import clsx from 'clsx';
import React from 'react';
import PropTypes from 'prop-types';
import { Container, Box, Divider, Grid, Typography, Button, Link } from '@mui/material';
import { ChevronRight, Check } from '@mui/icons-material';
import PageFooter from 'components/PageFooter';
import { useStyles } from './styles';
import Faq from 'components/Faq';
import LazyLoad from 'react-lazyload';
import useIsMobile from 'hooks/useIsMobile';
import PageFooterMobile from 'components/PageFooterMobile';

function StepIconComponent({ active, completed, icon }) {
  const classes = useStyles();

  return (
    <div
      className={clsx(classes.stepIcon, {
        [classes.stepIconActive]: active,
        [classes.stepIconCompleted]: completed,
      })}
    >
      {completed ? <Check /> : icon}
    </div>
  );
}

const Props = {
  onActionClick: PropTypes.func.isRequired,
};

export default function MainContent({ onActionClick }) {
  const classes = useStyles();
  const isMobile = useIsMobile();

  return (
    <>
      <div className={classes.lightBg}>
        <Container>
          <Box py={4}>
            <Grid container={true} spacing={2} alignItems="center">
              <Grid item={true} xs={12} md={6}>
                <Button
                  className={classes.buttonImage}
                  disableRipple={true}
                  onClick={onActionClick}
                >
                  <img
                    className={classes.sectionImage}
                    src="/images/poster/beautiful-poster-templates.webp"
                    alt="200+ Beautiful poster templates for every subject"
                    loading="lazy"
                    height="495"
                    width="600"
                  />
                </Button>
              </Grid>
              <Grid item={true} xs={12} md={6}>
                <Box px={6}>
                  <img
                    src="/images/poster/poster-templates-icon.svg"
                    alt="200+ Beautiful poster templates for every subject icon"
                    loading="lazy"
                    height="64"
                    width="69"
                  />

                  <Typography
                    component="h2"
                    variant="h6"
                    className={classes.primaryColor}
                    gutterBottom={true}
                  >
                    <b>Beautiful poster templates for every subject</b>
                  </Typography>
                  <Typography>
                    We changed the rules of the poster making experience. Start from an intuitive
                    template, fill the suggested spaces with your papers information, use exclusive
                    and scientifically accurate illustrations, customize elements (colors, shapes,
                    texts, and more), download, and be done!
                    <br /> <br />
                    Thanks to our online poster maker, create a perfect-looking scientific poster in
                    minutes even if you don’t have professional graphic design skills.
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>

          <Divider />

          <LazyLoad offset={100}>
            <Box py={4}>
              <Grid container={true} spacing={2} alignItems="center">
                <Grid item={true} xs={12} md={6}>
                  <Box px={6}>
                    <img
                      src="/images/poster/scientifically-accurate-posters-icon.svg"
                      alt="Scientifically accurate posters in just a few clicks icon"
                      loading="lazy"
                      height="78"
                      width="51"
                    />

                    <Typography
                      component="h2"
                      variant="h6"
                      className={classes.primaryColor}
                      gutterBottom={true}
                    >
                      <b>Scientifically accurate posters in just a few clicks</b>
                    </Typography>
                    <Typography>
                      Showcase your research and engage people with the help of our research poster
                      maker that lets you add images, charts, icons, and scientific illustrations to
                      your poster. Browse through 65000+ scientific illustrations from 80+ fields.
                    </Typography>
                  </Box>
                </Grid>
                <Grid item={true} xs={12} md={6}>
                  <Button
                    className={classes.buttonImage}
                    disableRipple={true}
                    onClick={onActionClick}
                  >
                    <img
                      className={classes.sectionImage}
                      src="/images/poster/scientifically-accurate-posters.webp"
                      alt="Scientifically accurate posters in just a few clicks"
                      loading="lazy"
                      height="468"
                      width="594"
                    />
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </LazyLoad>

          <LazyLoad offset={100}>
            <Divider />

            <Box py={4}>
              <Grid container={true} spacing={2} alignItems="center">
                <Grid item={true} xs={12} md={6}>
                  <Button
                    className={classes.buttonImage}
                    disableRipple={true}
                    onClick={onActionClick}
                  >
                    <img
                      className={classes.sectionImage}
                      src="/images/poster/poster-speaks-your-paper.webp"
                      alt="Create a scientific poster that speaks for your paper"
                      loading="lazy"
                      height="467"
                      width="600"
                    />
                  </Button>
                </Grid>
                <Grid item={true} xs={12} md={6}>
                  <Box px={6}>
                    <img
                      src="/images/poster/poster-speaks-your-paper-icon.svg"
                      alt="Create a scientific poster that speaks for your paper icon"
                      loading="lazy"
                      height="54"
                      width="70"
                    />

                    <Typography
                      component="h2"
                      variant="h6"
                      className={classes.primaryColor}
                      gutterBottom={true}
                    >
                      <b>Create a scientific poster that speaks for your paper</b>
                    </Typography>
                    <Typography>
                      Use captivating visuals, charts, and illustrations & bring life to your paper.{' '}
                      Make sure people recall your research after presenting.
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>

            <Divider />
          </LazyLoad>
        </Container>
      </div>

      <div className={classes.lightBg}>
        <Container>
          <Box py={6}>
            <Typography className={classes.title1} component="h2" variant="h6">
              People like you use Mind the Graph's Poster Maker
            </Typography>

            <LazyLoad offset={100}>
              <Grid container={true} spacing={2} alignItems="center">
                <Grid className={classes.avatarItem} item={true} xs={12} md={4}>
                  <div className={classes.avatarBox}>
                    <div>
                      <img
                        src="/images/poster/scientist-3.webp"
                        alt="Poster Maker user avatar"
                        loading="lazy"
                        height="100"
                        width="100"
                      />
                    </div>
                    <div className={classes.avatarBoxText}>
                      <Typography>
                        <b>Students</b>
                      </Typography>

                      <ul>
                        <li>Undergrad</li>
                        <li>Graduated</li>
                      </ul>
                    </div>
                  </div>
                </Grid>

                <Grid className={classes.avatarItem} item={true} xs={12} md={4}>
                  <div className={classes.avatarBox}>
                    <div>
                      <img
                        src="/images/poster/scientist-2.webp"
                        alt="Poster Maker user avatar"
                        loading="lazy"
                        height="100"
                        width="100"
                      />
                    </div>
                    <div className={classes.avatarBoxText}>
                      <Typography>
                        <b>Researchers</b>
                      </Typography>

                      <ul>
                        <li>Teacher</li>
                        <li>Investigator</li>
                      </ul>
                    </div>
                  </div>
                </Grid>

                <Grid className={classes.avatarItem} item={true} xs={12} md={4}>
                  <div className={classes.avatarBox}>
                    <div>
                      <img
                        src="/images/poster/scientist-1.webp"
                        alt="Poster Maker user avatar"
                        loading="lazy"
                        height="100"
                        width="100"
                      />
                    </div>
                    <div className={classes.avatarBoxText}>
                      <Typography>
                        <b>Director</b>
                      </Typography>

                      <ul>
                        <li>Laboratory leaders</li>
                      </ul>
                    </div>
                  </div>
                </Grid>
              </Grid>
            </LazyLoad>
          </Box>

          <LazyLoad offset={100}>
            <Divider />
          </LazyLoad>

          <LazyLoad offset={100}>
            <Box py={6}>
              <Typography
                className={classes.title2}
                component="h2"
                variant="h4"
                gutterBottom={true}
              >
                What is a scientific or research poster?
              </Typography>

              <div className={classes.infoBox}>
                <div className={classes.infoBoxContent}>
                  <div className={classes.infoBoxItem}>
                    <img
                      className={classes.infoBoxIcon}
                      src="/images/poster/poster-icon-flat.svg"
                      alt="Poster icon"
                      loading="lazy"
                      height="59"
                      width="65"
                    />

                    <Typography>
                      You probably already presented a scientific poster at an academic conference
                      or congress, right? If not, this may be your chance for the first time! Here's
                      our guide to academic poster presentation.
                    </Typography>

                    <Link
                      className={classes.showMeHow}
                      href="https://mindthegraph.com/blog/tutorial-video-creating-a-scientific-poster/"
                      target="_blank"
                      underline="hover"
                    >
                      Show me how! <ChevronRight className={classes.showMeHow} />
                    </Link>
                  </div>

                  <Divider
                    className={classes.verticalDivider}
                    orientation="vertical"
                    flexItem={true}
                  />

                  <div className={classes.infoBoxItem}>
                    <Typography className={classes.infoBoxText}>
                      A scientific poster or a research poster is an effective tool to communicate
                      your science and to present information about your research. The main goal of
                      these posters is to summarize the information and show it in a structured and
                      visualized way, thus enabling you to better interact with your audience in
                      fewer words.
                      <br /> <br /> Students, researchers, and scientists can easily make stunning
                      scientific posters without prior design experience.
                    </Typography>
                  </div>
                </div>
              </div>
            </Box>
          </LazyLoad>

          <LazyLoad offset={100}>
            <Box py={4}>
              <Grid container={true} spacing={2} alignItems="center">
                <Grid item={true} xs={12} md={6}>
                  <Button
                    className={classes.buttonImage}
                    disableRipple={true}
                    onClick={onActionClick}
                  >
                    <img
                      className={classes.sectionImage}
                      src="/images/poster/scientific-research-poster-templates.png"
                      alt="Scientific and research poster templates"
                      loading="lazy"
                      height="467"
                      width="600"
                    />
                  </Button>
                </Grid>
                <Grid item={true} xs={12} md={6}>
                  <Box px={6}>
                    <img
                      src="/images/poster/poster-scientific-research-templates-icon.svg"
                      alt="Scientific and research poster templates icon"
                      loading="lazy"
                      height="64"
                      width="60"
                    />

                    <Typography
                      component="h2"
                      variant="h6"
                      className={classes.primaryColor}
                      gutterBottom={true}
                    >
                      <b>Scientific and research poster templates</b>
                    </Typography>
                    <Typography>
                      Use Mind The Graph templates and stand out in your next conference! Your
                      science deserves to be attractive with eye-catching visuals. Make a scientific
                      poster, like award-winning scientific posters, in just a few clicks.
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </LazyLoad>
        </Container>
      </div>

      <LazyLoad offset={100}>
        <Box py={6} bgcolor="common.white">
          <Container>
            <Faq
              items={[
                {
                  question: 'What is a scientific poster?',
                  answer: [
                    'A scientific poster or a research poster is an effective tool to communicate your science and to present information about your research. The main goal of these posters is to summarize the information and show it in a structured and visualized way, thus enabling you to better interact with your audience in fewer words. An online scientific poster maker automates much of the designing and minimizes hard work to allow students, researchers and scientists to easily make stunning scientific posters without prior design experience.',
                  ],
                },
                {
                  question: 'What is scientific poster in research?',
                  answer: [
                    'Scientific poster is an infographic that contains all the main information about your research paper. Like magic, you can easily create one using Poster Maker. Click here and start creating now.',
                  ],
                },
                {
                  question: 'Why are scientific posters used?',
                  answer: [
                    'Scientific posters are used mostly because they are a great visual solution to present all the main information about your research paper in just one place.',
                  ],
                },
                {
                  question: 'What makes a good science poster?',
                  answer: [
                    'A good science poster must contain all the main information about your research paper, such as: introduction, methodology, major topics, conclusion, references, and images.',
                  ],
                },
              ]}
            />
          </Container>
        </Box>
      </LazyLoad>

      <LazyLoad offset={100}>
        <Box className={classes.darkBg}>
          <Container>
            <Typography className={classes.partnerLogo} variant="body2">
              Powered by{' '}
              <img
                src="/images/scholarcy-logo.svg"
                loading="lazy"
                height="20"
                width="83"
                alt="scholarcy logo"
              />
            </Typography>
          </Container>
        </Box>
      </LazyLoad>

      {!isMobile ? <PageFooter /> : <PageFooterMobile />}
    </>
  );
}

MainContent.propTypes = Props;
